// frontend/src/pages/Controls.jsx
// Module 2: Control Evidence Locker
// Displays all 14 governance controls for the org, grouped by APRA condition.
// Each control shows: APRA clause, status badge, evidence slot, owner, review dates.
// Compliance managers and admins can update status, assign owner, upload evidence.

import React, { useEffect, useState, useRef } from 'react';
import {
  getControls,
  updateControl,
  uploadEvidence,
  removeEvidence,
  downloadEvidence,
  getOrgUsers,
} from '../api';

// ---------------------------------------------------------------------------
// Status configuration — label, colour, icon character
// ---------------------------------------------------------------------------
const STATUS_CONFIG = {
  compliant: {
    label: 'Compliant',
    bg: '#e8f5e9',
    color: '#2e7d32',
    border: '#a5d6a7',
    dot: '●',
  },
  partial: {
    label: 'Partial',
    bg: '#fff8e1',
    color: '#f57f17',
    border: '#ffe082',
    dot: '◑',
  },
  non_compliant: {
    label: 'Non-Compliant',
    bg: '#fce4ec',
    color: '#c62828',
    border: '#ef9a9a',
    dot: '●',
  },
  not_assessed: {
    label: 'Not Assessed',
    bg: '#f5f5f5',
    color: '#616161',
    border: '#e0e0e0',
    dot: '○',
  },
};

const VALID_STATUSES = ['compliant', 'partial', 'non_compliant', 'not_assessed'];

// ---------------------------------------------------------------------------
// Helper: days until / overdue
// ---------------------------------------------------------------------------
function reviewLabel(dateStr) {
  if (!dateStr) return null;
  const due = new Date(dateStr);
  const today = new Date();
  const diffDays = Math.round((due - today) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return { text: `Overdue by ${Math.abs(diffDays)}d`, overdue: true };
  if (diffDays === 0) return { text: 'Due today', overdue: true };
  if (diffDays <= 30) return { text: `Due in ${diffDays}d`, overdue: false };
  return null;
}

// ---------------------------------------------------------------------------
// StatusBadge
// ---------------------------------------------------------------------------
function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.not_assessed;
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 5,
      padding: '3px 10px',
      borderRadius: 20,
      fontSize: 12,
      fontWeight: 600,
      background: cfg.bg,
      color: cfg.color,
      border: `1px solid ${cfg.border}`,
      whiteSpace: 'nowrap',
    }}>
      <span style={{ fontSize: 10 }}>{cfg.dot}</span>
      {cfg.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// ControlCard
// ---------------------------------------------------------------------------
function ControlCard({ control, users, onUpdated, canEdit }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    status: control.status || 'not_assessed',
    named_owner_id: control.named_owner_id || '',
    last_reviewed_at: control.last_reviewed_at ? control.last_reviewed_at.substring(0, 10) : '',
    next_review_due: control.next_review_due ? control.next_review_due.substring(0, 10) : '',
    notes: control.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef();

  const review = reviewLabel(control.next_review_due);

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      await updateControl(control.id, {
        status: form.status,
        named_owner_id: form.named_owner_id || null,
        last_reviewed_at: form.last_reviewed_at || null,
        next_review_due: form.next_review_due || null,
        notes: form.notes,
      });
      setEditing(false);
      onUpdated();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleFileUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      await uploadEvidence(control.id, file);
      onUpdated();
    } catch (e) {
      setError(e.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function handleRemoveEvidence() {
    if (!window.confirm('Remove the evidence file from this control? This cannot be undone.')) return;
    setError('');
    try {
      await removeEvidence(control.id);
      onUpdated();
    } catch (e) {
      setError(e.message);
    }
  }

  // The download runs as an authenticated request. The error is shown in an
  // alert because the card only displays its error inside the edit form.
  async function handleDownloadEvidence() {
    try {
      await downloadEvidence(control.id);
    } catch (e) {
      window.alert(e.message || 'Download failed.');
    }
  }

  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e8e3da',
      borderRadius: 8,
      marginBottom: 12,
      overflow: 'hidden',
    }}>
      {/* Header row */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 12,
        padding: '14px 18px 10px',
        borderBottom: editing ? '1px solid #e8e3da' : 'none',
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Condition code + APRA clause */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
            <span style={{
              fontSize: 11,
              fontWeight: 700,
              color: '#132e50',
              background: '#eef1f6',
              border: '1px solid #c5cfe0',
              borderRadius: 4,
              padding: '2px 7px',
              letterSpacing: 0.3,
            }}>
              {control.condition_code || '—'}
            </span>
            {control.apra_standard && (
              <span style={{
                fontSize: 11,
                color: '#b8860b',
                fontWeight: 600,
                background: '#fdf6e3',
                border: '1px solid #e8d49a',
                borderRadius: 4,
                padding: '2px 7px',
              }}>
                {control.apra_standard}
              </span>
            )}
            {control.apra_clause && (
              <span style={{ fontSize: 11, color: '#888' }}>
                {control.apra_clause}
              </span>
            )}
          </div>

          {/* Control name */}
          <div style={{ fontSize: 15, fontWeight: 600, color: '#1a1a1a', marginBottom: 3 }}>
            {control.control_name || control.condition_code}
          </div>

          {/* Description */}
          {control.description && (
            <div style={{ fontSize: 13, color: '#555', lineHeight: 1.5 }}>
              {control.description}
            </div>
          )}
        </div>

        {/* Status badge + review label */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 5, flexShrink: 0 }}>
          <StatusBadge status={control.status} />
          {review && (
            <span style={{
              fontSize: 11,
              color: review.overdue ? '#c62828' : '#e65100',
              fontWeight: 600,
            }}>
              {review.text}
            </span>
          )}
        </div>
      </div>

      {/* Metadata row — owner, dates, evidence */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 20,
        padding: '10px 18px',
        background: '#faf9f7',
        borderTop: '1px solid #f0ece4',
        fontSize: 12,
        color: '#666',
      }}>
        {/* Owner */}
        <div>
          <span style={{ color: '#999', marginRight: 4 }}>Owner:</span>
          <span style={{ color: '#333', fontWeight: 500 }}>
            {control.owner_name || 'Unassigned'}
          </span>
        </div>

        {/* Last reviewed */}
        <div>
          <span style={{ color: '#999', marginRight: 4 }}>Last reviewed:</span>
          <span style={{ color: '#333' }}>
            {control.last_reviewed_at
              ? new Date(control.last_reviewed_at).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })
              : '—'}
          </span>
        </div>

        {/* Next review */}
        <div>
          <span style={{ color: '#999', marginRight: 4 }}>Next review:</span>
          <span style={{ color: review?.overdue ? '#c62828' : '#333', fontWeight: review?.overdue ? 600 : 400 }}>
            {control.next_review_due
              ? new Date(control.next_review_due).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })
              : '—'}
          </span>
        </div>

        {/* Evidence slot */}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          {control.evidence_file_name ? (
            <>
              <span style={{ color: '#2e7d32', fontWeight: 600, fontSize: 11 }}>
                ✓ Evidence attached
              </span>
              <button
                type="button"
                onClick={handleDownloadEvidence}
                style={{
                  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                  fontFamily: 'inherit', color: '#132e50', fontWeight: 500,
                  textDecoration: 'underline', fontSize: 12,
                }}
              >
                {control.evidence_file_name}
              </button>
              <span style={{ color: '#999', fontSize: 11 }}>
                {control.evidence_uploaded_at
                  ? new Date(control.evidence_uploaded_at).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })
                  : ''}
              </span>
              {canEdit && (
                <button
                  onClick={handleRemoveEvidence}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#c62828',
                    cursor: 'pointer',
                    fontSize: 12,
                    padding: '0 4px',
                  }}
                  title="Remove evidence"
                >
                  ✕
                </button>
              )}
            </>
          ) : (
            <span style={{ color: '#999', fontStyle: 'italic', fontSize: 12 }}>No evidence attached</span>
          )}

          {/* Upload button */}
          {canEdit && (
            <>
              <input
                type="file"
                ref={fileRef}
                onChange={handleFileUpload}
                accept=".pdf,.doc,.docx,.xlsx,.png,.jpg,.jpeg"
                style={{ display: 'none' }}
                id={`ev-upload-${control.id}`}
              />
              <label
                htmlFor={`ev-upload-${control.id}`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '4px 10px',
                  background: '#132e50',
                  color: '#fff',
                  borderRadius: 5,
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  opacity: uploading ? 0.6 : 1,
                  whiteSpace: 'nowrap',
                }}
              >
                {uploading ? 'Uploading…' : '↑ Attach Evidence'}
              </label>
            </>
          )}
        </div>
      </div>

      {/* Edit button */}
      {canEdit && !editing && (
        <div style={{ padding: '8px 18px', borderTop: '1px solid #f0ece4' }}>
          <button
            onClick={() => setEditing(true)}
            style={{
              background: 'none',
              border: '1px solid #c5cfe0',
              borderRadius: 5,
              color: '#132e50',
              fontSize: 12,
              fontWeight: 600,
              padding: '4px 12px',
              cursor: 'pointer',
            }}
          >
            Edit Status &amp; Details
          </button>
        </div>
      )}

      {/* Edit form */}
      {editing && (
        <div style={{ padding: '16px 18px', borderTop: '1px solid #e8e3da', background: '#fff' }}>
          {error && (
            <div style={{
              background: '#fce4ec',
              border: '1px solid #ef9a9a',
              color: '#c62828',
              borderRadius: 5,
              padding: '8px 12px',
              marginBottom: 12,
              fontSize: 13,
            }}>
              {error}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
            {/* Status */}
            <div>
              <label style={labelStyle}>Status</label>
              <select
                value={form.status}
                onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                style={inputStyle}
              >
                {VALID_STATUSES.map(s => (
                  <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>
                ))}
              </select>
            </div>

            {/* Owner */}
            <div>
              <label style={labelStyle}>Named Owner</label>
              <select
                value={form.named_owner_id}
                onChange={e => setForm(f => ({ ...f, named_owner_id: e.target.value }))}
                style={inputStyle}
              >
                <option value="">— Unassigned —</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            </div>

            {/* Last reviewed */}
            <div>
              <label style={labelStyle}>Last Reviewed</label>
              <input
                type="date"
                value={form.last_reviewed_at}
                onChange={e => setForm(f => ({ ...f, last_reviewed_at: e.target.value }))}
                style={inputStyle}
              />
            </div>

            {/* Next review */}
            <div>
              <label style={labelStyle}>Next Review Due</label>
              <input
                type="date"
                value={form.next_review_due}
                onChange={e => setForm(f => ({ ...f, next_review_due: e.target.value }))}
                style={inputStyle}
              />
            </div>

            {/* Notes — spans 2 cols */}
            <div style={{ gridColumn: 'span 2' }}>
              <label style={labelStyle}>Notes</label>
              <textarea
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                rows={2}
                placeholder="Optional notes for auditors or board review"
                style={{ ...inputStyle, resize: 'vertical' }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                background: '#b8860b',
                color: '#fff',
                border: 'none',
                borderRadius: 5,
                padding: '7px 18px',
                fontWeight: 700,
                fontSize: 13,
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.7 : 1,
              }}
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
            <button
              onClick={() => { setEditing(false); setError(''); }}
              style={{
                background: 'none',
                border: '1px solid #ccc',
                borderRadius: 5,
                padding: '7px 18px',
                fontWeight: 600,
                fontSize: 13,
                cursor: 'pointer',
                color: '#555',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Shared input styles — consistent with vendor register
const labelStyle = {
  display: 'block',
  fontSize: 11,
  fontWeight: 700,
  color: '#132e50',
  marginBottom: 4,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
};

const inputStyle = {
  width: '100%',
  padding: '7px 10px',
  border: '1px solid #d8d3ca',
  borderRadius: 5,
  fontSize: 13,
  color: '#1a1a1a',
  background: '#fff',
  boxSizing: 'border-box',
  fontFamily: 'DM Sans, sans-serif',
};

// ---------------------------------------------------------------------------
// Status summary bar
// ---------------------------------------------------------------------------
function SummaryBar({ controls }) {
  const counts = VALID_STATUSES.reduce((acc, s) => {
    acc[s] = controls.filter(c => (c.status || 'not_assessed') === s).length;
    return acc;
  }, {});
  const total = controls.length;

  return (
    <div style={{
      display: 'flex',
      gap: 12,
      flexWrap: 'wrap',
      background: '#fff',
      border: '1px solid #e8e3da',
      borderRadius: 8,
      padding: '14px 20px',
      marginBottom: 24,
    }}>
      {VALID_STATUSES.map(s => {
        const cfg = STATUS_CONFIG[s];
        const pct = total > 0 ? Math.round((counts[s] / total) * 100) : 0;
        return (
          <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{
              display: 'inline-block',
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: cfg.color,
            }} />
            <span style={{ fontSize: 13, color: '#555' }}>{cfg.label}</span>
            <span style={{ fontSize: 16, fontWeight: 700, color: '#132e50' }}>{counts[s]}</span>
            <span style={{ fontSize: 11, color: '#aaa' }}>({pct}%)</span>
          </div>
        );
      })}
      <div style={{ marginLeft: 'auto', fontSize: 12, color: '#999', alignSelf: 'center' }}>
        {controls.filter(c => c.evidence_file_name).length} of {total} with evidence attached
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Controls page
// ---------------------------------------------------------------------------
export default function Controls({ user }) {
  const [controls, setControls] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterStandard, setFilterStandard] = useState('all');

  const canEdit = user && ['admin', 'compliance_manager'].includes(user.role);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const [c, u] = await Promise.all([getControls(), getOrgUsers()]);
      setControls(c);
      setUsers(u);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  // Derive available standards for filter
  const standards = ['all', ...new Set(controls.map(c => c.apra_standard).filter(Boolean))];

  // Apply filters
  const filtered = controls.filter(c => {
    const matchStatus = filterStatus === 'all' || (c.status || 'not_assessed') === filterStatus;
    const matchStandard = filterStandard === 'all' || c.apra_standard === filterStandard;
    return matchStatus && matchStandard;
  });

  // Group by condition_code prefix (e.g. "CPS230-1" → "CPS230")
  const grouped = filtered.reduce((acc, c) => {
    const group = c.condition_code ? c.condition_code.split('-')[0] : 'Other';
    if (!acc[group]) acc[group] = [];
    acc[group].push(c);
    return acc;
  }, {});

  return (
    <div style={{ padding: '32px 40px', maxWidth: 1100, margin: '0 auto' }}>

      {/* Page header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 26, fontWeight: 700, color: '#132e50', margin: 0 }}>
          Control Evidence Locker
        </h1>
        <p style={{ marginTop: 6, color: '#666', fontSize: 14 }}>
          APRA-mapped governance controls — status, evidence, and ownership for audit-ready compliance.
        </p>
      </div>

      {error && (
        <div style={{
          background: '#fce4ec',
          border: '1px solid #ef9a9a',
          color: '#c62828',
          borderRadius: 6,
          padding: '10px 14px',
          marginBottom: 20,
          fontSize: 13,
        }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#999', fontSize: 15 }}>
          Loading controls…
        </div>
      ) : (
        <>
          {/* Summary bar */}
          <SummaryBar controls={controls} />

          {/* Filters */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
            <div>
              <label style={{ ...labelStyle, display: 'inline', marginRight: 8 }}>Status:</label>
              <select
                value={filterStatus}
                onChange={e => setFilterStatus(e.target.value)}
                style={{ ...inputStyle, width: 'auto', display: 'inline-block' }}
              >
                <option value="all">All Statuses</option>
                {VALID_STATUSES.map(s => (
                  <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ ...labelStyle, display: 'inline', marginRight: 8 }}>Standard:</label>
              <select
                value={filterStandard}
                onChange={e => setFilterStandard(e.target.value)}
                style={{ ...inputStyle, width: 'auto', display: 'inline-block' }}
              >
                {standards.map(s => (
                  <option key={s} value={s}>{s === 'all' ? 'All Standards' : s}</option>
                ))}
              </select>
            </div>
            <button
              onClick={load}
              style={{
                marginLeft: 'auto',
                background: 'none',
                border: '1px solid #c5cfe0',
                borderRadius: 5,
                color: '#132e50',
                fontSize: 12,
                fontWeight: 600,
                padding: '6px 14px',
                cursor: 'pointer',
              }}
            >
              ↻ Refresh
            </button>
          </div>

          {/* Controls grouped by condition prefix */}
          {Object.keys(grouped).sort().map(group => (
            <div key={group} style={{ marginBottom: 28 }}>
              <div style={{
                fontSize: 13,
                fontWeight: 700,
                color: '#132e50',
                letterSpacing: 0.5,
                textTransform: 'uppercase',
                marginBottom: 10,
                borderBottom: '2px solid #e8e3da',
                paddingBottom: 6,
              }}>
                {group}
                <span style={{ fontWeight: 400, color: '#999', marginLeft: 8, fontSize: 12 }}>
                  ({grouped[group].length} control{grouped[group].length !== 1 ? 's' : ''})
                </span>
              </div>
              {grouped[group].map(c => (
                <ControlCard
                  key={c.id}
                  control={c}
                  users={users}
                  onUpdated={load}
                  canEdit={canEdit}
                />
              ))}
            </div>
          ))}

          {filtered.length === 0 && !loading && (
            <div style={{ textAlign: 'center', padding: 60, color: '#999', fontSize: 14 }}>
              No controls match the selected filters.
            </div>
          )}
        </>
      )}
    </div>
  );
}
